/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_STATUS } from './constants/listingStatus';
import { matchesChannelId, updateListingVariantInState } from './helpers/channelHelpers';
import {
  findListingEntity,
  getListingProductIdFromState,
  listListingVariantsForChannel,
} from './helpers/entityReadHelpers';
import { upsertListing, upsertListingVariant } from './helpers/entityAdapter';
import { showDraftForChannelToast } from './helpers/showDraftForChannelToast';
import { setActiveTabByProductChannel } from './helpers/uiAdapter';

export const createListingDraftFromPublished = createAsyncThunk(
  'products/createListingDraftFromPublished',
  async ({ productId, channelIds, masterVariantIds }, { getState, extra: { sdk, Toast } }) => {
    const productsState = getState().products;
    const product = productsState.items.find(({ _id }) => _id === productId);

    const results = await Promise.all(
      channelIds.map(async (channelId) => {
        const listingProductId = getListingProductIdFromState(productsState, productId, channelId);
        if (!listingProductId) return null;

        const { data: updatedListing } = await sdk.products.updateListing(listingProductId, {
          listingStatus: LISTING_STATUS.DRAFT,
        });

        const channelVariants = listListingVariantsForChannel(productsState, productId, channelId);
        const variantsToProcess = masterVariantIds?.length
          ? channelVariants.filter(({ masterVariantId }) => masterVariantIds.includes(masterVariantId))
          : channelVariants;

        const updatedVariants = await Promise.all(
          variantsToProcess.map((entry) => (
            sdk.products.updateVariant(listingProductId, entry._id, {
              listingStatus: LISTING_STATUS.DRAFT,
            }).then(({ data }) => data)
          )),
        );

        return { channelId, updatedListing, updatedVariants };
      }),
    );

    const validResults = results.filter(Boolean);

    const { user: { team } } = getState();

    showDraftForChannelToast({
      Toast,
      channelIds,
      salesChannels: team?.salesChannels || [],
    });

    return { productId, results: validResults };
  },
);

export const reducer = (builder) => {
  builder
    .addCase(createListingDraftFromPublished.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(createListingDraftFromPublished.fulfilled, (state, { payload }) => {
      const { productId, results } = payload;
      state.isBusy = false;

      results.forEach(({ channelId, updatedListing, updatedVariants }) => {
        const existingListing = findListingEntity(state, { masterProductId: productId, channelId });
        if (!existingListing) return;

        setActiveTabByProductChannel(state, productId, channelId, LISTING_STATUS.DRAFT);
        upsertListing(state, {
          ...existingListing,
          ...updatedListing,
          listingStatus: LISTING_STATUS.DRAFT,
          masterProductId: productId,
        });

        updatedVariants.forEach((updatedVariant) => {
          updateListingVariantInState(state, { masterProductId: productId, channelId, data: updatedVariant });
          upsertListingVariant(state, {
            ...(state.entities?.listingVariants?.[updatedVariant._id] || {}),
            ...updatedVariant,
          });
        });
      });
    })
    .addCase(createListingDraftFromPublished.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot create draft from published listing';
    });
};
